import {Inject, Injectable, LOCALE_ID, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {IntlProxyPP} from './intl.proxy';
import {takeUntilDestroy} from 'take-until-destroy';
import {Language} from '../interfaces/language';
import {CldrIntlService, IntlService} from '@progress/kendo-angular-intl';
import {LogManager} from '../../log/log.manager';
import {LogCat} from '../../log/log.categories';
import {AuthProviderService} from '../../../app-auth/services/auth-provider.service';
import {filter} from 'rxjs/operators';
import {RtlService} from './rtl.service';
import { DOCUMENT } from '@angular/common';

export interface LanguageAndManualSelect extends Language {
	manualSelect?: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class IntlServicePP implements OnDestroy {
	public cachedLanguages: Language[];
	public usedLanguage: Language;
	public usedLanguageSubject: BehaviorSubject<LanguageAndManualSelect>;
	public defaultBrowserLanguage: Language;

	constructor(@Inject(LOCALE_ID) public localeId: string,
				@Inject(DOCUMENT) private document: Document,
				public intlService: IntlService,
				private intlProxyPP: IntlProxyPP,
				private authProviderService: AuthProviderService,
				private rtlService: RtlService) {
		this.usedLanguageSubject = new BehaviorSubject(null);

		this.getLanguages();
	}

	/*
	   * Returns the first week day index starting from Sunday (0 == Sunday).
	   */
	public get firstWeekDay(): number {
		return (this.intlService as CldrIntlService).firstDay();
	}

	public getDefaultLanguage(languages: Language[]): Language {
		let language: Language;

		const navigatorLanguage: string = (navigator.language || (navigator as any).userLanguage).toLowerCase();
		language = languages.find(l => l.code.toLowerCase() === navigatorLanguage);
		if (language) {
			this.defaultBrowserLanguage = language;
			return language;
		}

		// Get the browser language without location.
		language = languages.find(l => l.code.toLowerCase() === navigatorLanguage.split('-')[0]);
		if (language) {
			this.defaultBrowserLanguage = language;
			return language;
		}

		// Get the defined default system language.
		language = languages.find(l => l.isDefault);
		if (language) {
			LogManager.debug(LogCat.Intl.GetDefaultLanguage,
				'The browser language is not supported. Setting to the configured default language.');
			this.defaultBrowserLanguage = language;
			return language;
		}

		// Get the defined English language.
		language = languages.find(l => l.languageKey === 1000);
		if (language) {
			LogManager.error(LogCat.Intl.GetDefaultLanguage,
				'The browser and configured default languages are not supported. Setting to pre-defined English.');
			this.defaultBrowserLanguage = language;
			return language;
		}

		// Get the first defined active language.
		LogManager.error(LogCat.Intl.GetDefaultLanguage,
			'The browser, configured default and pre-defined English languages are not supported. Setting to first defined active language.');
		language = languages.filter(l => l.isActive).length ? languages.filter(l => l.isActive)[0] : languages[0];
		this.defaultBrowserLanguage = language;
		return language;
	}

	ngOnDestroy(): void { }

	public getLanguages() {
		this.intlProxyPP.getLanguages().pipe(takeUntilDestroy(this)).subscribe(res => {
			this.cachedLanguages = res;
			if (this.authProviderService.isAuthenticated()) {
				const langToSet = this.cachedLanguages
					.find(l => l.languageKey === this.authProviderService.currentPatient.accountPreferences.defaultLanguageKey);
				this.setLanguage(langToSet ? langToSet : this.getDefaultLanguage(res), false, true);
			} else {
				this.setLanguage(this.getDefaultLanguage(res), false, true);
			}
		}, (error) => {
			LogManager.debug(LogCat.SelfScheduling.General, "Unknown error", error);
		});
	}

	public getUsedLanguageObservable(): Observable<LanguageAndManualSelect> {
		return this.usedLanguageSubject.asObservable().pipe(filter(x => x !== undefined && x !== null));
	}

	public setLanguage(language: Language, loginInProgress: boolean = false, fromManualSelection= false) {
		if (language.rightToLeft !== this.rtlService.isRtl()) {
			this.rtlService.setDir(language, loginInProgress);
		}
		this.setLocale(language.kendoCode);
		this.usedLanguageSubject.next(Object.assign(language, { manualSelect: fromManualSelection}));
		this.usedLanguage = language;

		// Update the global <html> element lang attribute
		this.document.documentElement.lang = language.code;
	}

	public setLocale(locale: string): void {
		this.localeId = locale;
		(this.intlService as CldrIntlService).localeId = locale;
	}
}
